import {
    InteractionLayerSizeV2,
    LayerHandleV2,
    LayerManagerV2,
    ShimBackgroundTypeV2,
    CloseHandleV2
} from '@volkswagen-onehub/layer-manager';
import * as React from 'react';
import {PersonalizationInclude} from '../../components/PersonalizationInclude';
import {inject, singleton} from '../../infrastructure/di/annotations';
import {RichtextSimpleElement} from '../../modules/editorial/elements/RichtextElement';
import {Value, ValueFactory} from '../../utils/contextUtils';
import {DisclaimerStore} from '../disclaimer/DisclaimerStore';
import {DisclaimerStoreContext} from '../disclaimer/DisclaimerStoreContext';
import {GlobalDisclaimerStore} from '../disclaimer/GlobalDisclaimerStore';
import {makeClip} from '../../d6/components/helpers';
import {
    XfCarFeatureModel,
    XfContentLayerMainModel
} from '../../../generated/core';
import {styled} from '@volkswagen-onehub/components-core';
import {parseHeadingRichtextToString} from '../../utils/parseHeadingRichtextToString';

export type LayerState = {
    forceClose?: boolean;
};

export type ContentLayerData = XfContentLayerMainModel | XfCarFeatureModel;

export type RenderContentLayerData = Pick<
    ContentLayerData,
    'btnCloseLabel' | 'cqItems' | 'cqType' | 'overlineLabel'
>;

const StyledSrOnlyOverline = styled.span`
    ${makeClip}
`;

@singleton('LayerRenderer')
export class LayerRenderer {
    @inject() private globalDisclaimerStore!: GlobalDisclaimerStore;
    @inject() private layerManager!: LayerManagerV2;
    private disclaimerContextValueFactory: ValueFactory<DisclaimerStore> =
        new ValueFactory<DisclaimerStore>();
    public renderInteractionLayer(
        includePath: string,
        cqType: string,
        onClose?: CloseHandleV2<LayerState, void>
    ): LayerHandleV2<LayerState> {
        const store = this.globalDisclaimerStore.getLayeredStore(includePath);
        const value = this.disclaimerContextValueFactory.create(store);

        return this.layerManager.openInteractionLayer(
            this.renderLayer(includePath, cqType, value),
            {},
            {
                size: InteractionLayerSizeV2.C,
                shimBackgroundType: ShimBackgroundTypeV2.OPAQUE,
                onClose,
                userCloseable: true
            }
        );
    }
    public renderContentLayer(
        includePath: string,
        contentLayerData: RenderContentLayerData,
        contentHeadlinePath: string,
        onClose?: CloseHandleV2<LayerState, void>
    ): LayerHandleV2<LayerState> {
        const store = this.globalDisclaimerStore.getLayeredStore(includePath);
        const value = this.disclaimerContextValueFactory.create(store);
        const {btnCloseLabel, cqItems, cqType, overlineLabel} =
            contentLayerData;

        const richtext =
            cqItems?.overline?.richtext ?? cqItems?.title?.richtext;
        const overlineHasText = !!parseHeadingRichtextToString(richtext);

        const visibleOverline = (
            <DisclaimerStoreContext.Provider value={value}>
                <RichtextSimpleElement path={contentHeadlinePath} />
            </DisclaimerStoreContext.Provider>
        );

        const srOnlyOverline = (
            <StyledSrOnlyOverline>{overlineLabel}</StyledSrOnlyOverline>
        );

        const contentHeadline = overlineHasText
            ? visibleOverline
            : srOnlyOverline;

        return this.layerManager.openContentLayerV2(
            this.renderLayer(includePath, cqType, value),
            {},
            {
                'aria-labelledby': 'content-layer-id',
                closeButtonLabel: btnCloseLabel,
                contentHeadline,
                disableDefaultPadding: false,
                onClose,
                userCloseable: true
            }
        );
    }

    private renderLayer =
        (
            includePath: string,
            cqType: string,
            disclaimerStoreValue: Value<DisclaimerStore>
        ) =>
        (): React.ReactElement => {
            return (
                <DisclaimerStoreContext.Provider value={disclaimerStoreValue}>
                    <PersonalizationInclude
                        path={includePath}
                        resourceType={cqType}
                    />
                </DisclaimerStoreContext.Provider>
            );
        };
}
